import React, { useState } from 'react'
import { Link } from 'react-router-dom'

function ConsentDetailModal({ item, onClose }) {
  if (!item) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="닫기"
        onClick={onClose}
      />
      <div
        className="relative flex max-h-[min(88vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="health-consent-detail-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <h3 id="health-consent-detail-title" className="text-base font-bold text-slate-900">
            {item.label}
          </h3>
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            onClick={onClose}
          >
            닫기
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {item.summary ? (
            <p className="text-sm leading-relaxed text-slate-600">{item.summary}</p>
          ) : null}
          {item.bodyHtml ? (
            <div
              className="prose prose-sm mt-3 max-w-none text-sm leading-relaxed text-slate-600 prose-headings:text-sm prose-headings:font-semibold prose-ul:my-1 prose-p:my-1"
              dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
            />
          ) : null}
          {!item.bodyHtml && item.detail?.length ? (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-600">
              {item.detail.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function HealthDataConsentFields({ items, checked, onChange, disabled, loading }) {
  const [detailItem, setDetailItem] = useState(null)
  const list = Array.isArray(items) ? items : []

  if (loading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">동의 항목을 불러오는 중…</div>
  }
  if (!list.length) {
    return <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">동의 항목을 불러오지 못했습니다.</div>
  }

  const toggleAll = (next) => {
    const patch = Object.fromEntries(list.map((item) => [item.id, next]))
    onChange?.({ ...checked, ...patch })
  }

  const allChecked = list.every((item) => checked?.[item.id])
  const someChecked = list.some((item) => checked?.[item.id])

  return (
    <>
      <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900">필수 동의</div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              className="rounded border-slate-300"
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = someChecked && !allChecked
              }}
              onChange={(e) => toggleAll(e.target.checked)}
              disabled={disabled}
            />
            전체 동의
          </label>
        </div>

        <ul className="space-y-2">
          {list.map((item) => (
            <li key={item.id} className="rounded-md border border-slate-100 bg-slate-50/80 px-3 py-2">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-slate-300"
                  checked={!!checked?.[item.id]}
                  onChange={(e) => onChange?.({ ...checked, [item.id]: e.target.checked })}
                  disabled={disabled}
                  required={item.required !== false}
                  id={`health-consent-${item.id}`}
                />
                <div className="min-w-0 flex-1">
                  <label htmlFor={`health-consent-${item.id}`} className="cursor-pointer text-sm font-medium text-slate-800">
                    {item.label}
                    {item.required !== false ? <span className="ml-1 text-rose-600">*</span> : null}
                  </label>
                </div>
                {(item.bodyHtml || item.detail?.length || item.summary) ? (
                  <button
                    type="button"
                    className="shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={() => setDetailItem(item)}
                  >
                    상세보기
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>

        <p className="text-xs text-slate-500">
          플랫폼{' '}
          <Link className="text-brand-navy hover:underline" to="/terms" target="_blank" rel="noopener noreferrer">
            이용약관
          </Link>
          {' · '}
          <Link className="text-brand-navy hover:underline" to="/privacy" target="_blank" rel="noopener noreferrer">
            개인정보처리방침
          </Link>
          은 회원 가입 시 동의한 내용을 따릅니다.
        </p>
      </div>
      <ConsentDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
    </>
  )
}
